import { LinearGradient } from 'expo-linear-gradient';
import { Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react-native';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import {
  Dimensions,
  type LayoutChangeEvent,
  Modal,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  type ViewStyle,
} from 'react-native';

type KeyboardEvent = { key: string; preventDefault: () => void };

import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { computeMenuPlacement, type MenuPlacement } from './Select.placement';

const MENU_MAX_HEIGHT = 320;
const MENU_GAP = 4;

interface TriggerRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SelectOption {
  value: string;
  label: string;
  color?: string;
  icon?: ReactNode;
}

interface SelectBaseProps {
  options: SelectOption[];
  placeholder?: string;
  style?: ViewStyle;
  testID?: string;
  accessibilityLabel?: string;
  /**
   * Show a filter box above the options (FLOSEAI-670). Turn it on for lists
   * that grow with the organisation's data — leads, agents, teammates — and
   * leave it off for fixed sets of choices, where a search box is just noise.
   */
  searchable?: boolean;
  searchPlaceholder?: string;
  emptySearchLabel?: string;
}

interface SingleSelectProps extends SelectBaseProps {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
}

interface MultiSelectProps extends SelectBaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

type ChipsSelectProps = (SingleSelectProps | MultiSelectProps) & {
  variant: 'chips';
};

type DropdownSelectProps = (SingleSelectProps | MultiSelectProps) & {
  variant: 'dropdown';
};

type SelectProps = ChipsSelectProps | DropdownSelectProps;

/**
 * Fold case and accents so "gomez" finds "Gómez". Most of this product's names
 * and cities carry accents that nobody types into a filter box.
 */
function normalizeForSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Phone numbers are not stored in one shape: the same list shows
 * `+573001112233` next to `+57-302-667-8811` (FLOSEAI-622). A user reads the
 * digits off the screen and types them without separators, so digits are
 * compared to digits as well as text to text.
 */
function digitsOf(text: string): string {
  return text.replace(/\D/g, '');
}

/** Below this, a digit run is too short to mean a phone number. */
const MIN_DIGIT_QUERY = 4;

export function filterSelectOptions<T extends { label: string }>(
  options: readonly T[],
  query: string,
): T[] {
  const needle = normalizeForSearch(query);
  if (!needle) return [...options];

  const needleDigits = digitsOf(query);
  const matchesByDigits = needleDigits.length >= MIN_DIGIT_QUERY;

  return options.filter((option) => {
    if (normalizeForSearch(option.label).includes(needle)) return true;
    return matchesByDigits && digitsOf(option.label).includes(needleDigits);
  });
}

function isSelected(value: string | string[], optionValue: string): boolean {
  if (Array.isArray(value)) {
    return value.includes(optionValue);
  }
  return value === optionValue;
}

const CHIPS_OVERFLOW_THRESHOLD = 1;

function ChipsVariant({
  options,
  value,
  onChange,
  multiple,
  style,
  testID,
}: Readonly<Omit<SelectProps, 'variant'>>) {
  const scrollRef = useRef<ScrollView>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [offset, setOffset] = useState(0);

  const handlePress = useCallback(
    (optionValue: string) => {
      if (multiple && Array.isArray(value)) {
        const multiOnChange = onChange as (value: string[]) => void;
        if (value.includes(optionValue)) {
          multiOnChange(value.filter((v) => v !== optionValue));
        } else {
          multiOnChange([...value, optionValue]);
        }
      } else {
        const singleOnChange = onChange as (value: string) => void;
        singleOnChange(optionValue);
      }
    },
    [value, onChange, multiple],
  );

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  const handleContentSizeChange = useCallback((width: number) => {
    setContentWidth(width);
  }, []);

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    setOffset(event.nativeEvent.contentOffset.x);
  }, []);

  const maxOffset = Math.max(0, contentWidth - containerWidth);
  const canScrollLeft = offset > CHIPS_OVERFLOW_THRESHOLD;
  const canScrollRight = maxOffset - offset > CHIPS_OVERFLOW_THRESHOLD;

  const scrollByPage = useCallback(
    (direction: -1 | 1) => {
      const page = containerWidth > 0 ? containerWidth * 0.75 : 200;
      const next = Math.max(0, Math.min(offset + direction * page, maxOffset));
      scrollRef.current?.scrollTo?.({ x: next, y: 0, animated: true });
      setOffset(next);
    },
    [containerWidth, offset, maxOffset],
  );

  return (
    <View style={chipStyles.container}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[chipStyles.scrollContent, style]}
        onLayout={handleLayout}
        onContentSizeChange={handleContentSizeChange}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        testID={testID}
      >
        {options.map((option) => {
          const active = isSelected(value, option.value);
          return (
            <Pressable
              key={option.value}
              onPress={() => handlePress(option.value)}
              style={({ pressed }) => [
                chipStyles.chip,
                active ? chipStyles.chipActive : chipStyles.chipInactive,
                pressed && chipStyles.chipPressed,
              ]}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              testID={testID ? `${testID}-chip-${option.value}` : undefined}
            >
              {option.icon && <View style={chipStyles.chipIcon}>{option.icon}</View>}
              <Text
                style={[
                  chipStyles.chipText,
                  active ? chipStyles.chipTextActive : chipStyles.chipTextInactive,
                ]}
                numberOfLines={1}
              >
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>

      {canScrollLeft && (
        <Pressable
          onPress={() => scrollByPage(-1)}
          style={[chipStyles.affordance, chipStyles.affordanceLeft]}
          accessibilityRole="button"
          accessibilityLabel="Scroll left for more options"
          testID={testID ? `${testID}-scroll-left` : undefined}
        >
          <LinearGradient
            colors={[colors.background.primary, 'transparent']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={StyleSheet.absoluteFill}
            pointerEvents="none"
          />
          <ChevronLeft size={16} color={colors.text.secondary} strokeWidth={2.5} />
        </Pressable>
      )}

      {canScrollRight && (
        <Pressable
          onPress={() => scrollByPage(1)}
          style={[chipStyles.affordance, chipStyles.affordanceRight]}
          accessibilityRole="button"
          accessibilityLabel="Scroll right for more options"
          testID={testID ? `${testID}-scroll-right` : undefined}
        >
          <LinearGradient
            colors={['transparent', colors.background.primary]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={StyleSheet.absoluteFill}
            pointerEvents="none"
          />
          <ChevronRight size={16} color={colors.text.secondary} strokeWidth={2.5} />
        </Pressable>
      )}
    </View>
  );
}

function DropdownVariant({
  options,
  value,
  onChange,
  multiple,
  placeholder = 'Select...',
  style,
  testID,
  accessibilityLabel,
  searchable = false,
  searchPlaceholder = 'Search…',
  emptySearchLabel = 'No matches',
}: Readonly<Omit<SelectProps, 'variant'>>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [placement, setPlacement] = useState<MenuPlacement>({
    dropUp: false,
    maxHeight: MENU_MAX_HEIGHT,
  });
  const [anchor, setAnchor] = useState<TriggerRect | null>(null);
  const containerRef = useRef<View>(null);

  // Everything the menu shows and the keyboard walks is the FILTERED list;
  // navigating a list you cannot see is how a filter picks the wrong row.
  const visibleOptions = searchable ? filterSelectOptions(options, query) : options;

  const selectedLabels = options
    .filter((opt) => isSelected(value, opt.value))
    .map((opt) => opt.label);

  const displayText = selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder;

  const handleSelect = useCallback(
    (optionValue: string) => {
      if (multiple && Array.isArray(value)) {
        const multiOnChange = onChange as (value: string[]) => void;
        if (value.includes(optionValue)) {
          multiOnChange(value.filter((v) => v !== optionValue));
        } else {
          multiOnChange([...value, optionValue]);
        }
      } else {
        const singleOnChange = onChange as (value: string) => void;
        singleOnChange(optionValue);
        setOpen(false);
      }
    },
    [value, onChange, multiple],
  );

  const handleToggle = useCallback(() => {
    if (open) {
      setOpen(false);
      return;
    }
    setHighlightedIndex(-1);
    // A query left over from last time would silently hide most of the list.
    setQuery('');
    const node = containerRef.current;
    const measure = node?.measureInWindow?.bind(node);
    if (measure) {
      measure((x, y, width, height) => {
        setAnchor({ x, y, width, height });
        setPlacement(
          computeMenuPlacement({
            triggerY: y,
            triggerHeight: height,
            windowHeight: Dimensions.get('window').height,
            optionCount: options.length,
            hardCap: MENU_MAX_HEIGHT,
          }),
        );
        setOpen(true);
      });
    } else {
      setOpen(true);
    }
  }, [open, options.length]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setHighlightedIndex(0);
          setOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < visibleOptions.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : visibleOptions.length - 1));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < visibleOptions.length) {
            handleSelect(visibleOptions[highlightedIndex].value);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
        case 'Home':
          e.preventDefault();
          setHighlightedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setHighlightedIndex(visibleOptions.length - 1);
          break;
      }
    },
    [open, visibleOptions, highlightedIndex, handleSelect],
  );

  return (
    <View
      ref={containerRef}
      style={[dropdownStyles.wrapper, style]}
      testID={testID}
      {...(Platform.OS === 'web' ? { onKeyDown: handleKeyDown } : {})}
    >
      <Pressable
        onPress={handleToggle}
        style={({ pressed }) => [
          dropdownStyles.trigger,
          open && dropdownStyles.triggerOpen,
          pressed && dropdownStyles.triggerPressed,
        ]}
        accessibilityRole="combobox"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ expanded: open }}
        testID={testID ? `${testID}-trigger` : undefined}
      >
        <Text
          style={[
            dropdownStyles.triggerText,
            selectedLabels.length === 0 && dropdownStyles.triggerPlaceholder,
          ]}
          numberOfLines={1}
        >
          {displayText}
        </Text>
        <ChevronDown
          size={16}
          color={colors.text.tertiary}
          style={open ? dropdownStyles.chevronOpen : undefined}
        />
      </Pressable>

      <Modal transparent visible={open} animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable
          style={dropdownStyles.modalBackdrop}
          onPress={() => setOpen(false)}
          accessibilityLabel="Close menu"
        />
        {/*
          Positioned against the trigger when it could be measured. Without a
          measurement the menu is still rendered, unanchored: measureInWindow is
          missing on some targets, and there the dropdown used to open onto an
          empty overlay with no way to pick anything.
        */}
        <View
          style={[
            dropdownStyles.menu,
            anchor && dropdownStyles.menuFloating,
            anchor
              ? {
                  left: anchor.x,
                  width: anchor.width,
                  ...(placement.dropUp
                    ? { bottom: Dimensions.get('window').height - anchor.y + MENU_GAP }
                    : { top: anchor.y + anchor.height + MENU_GAP }),
                }
              : dropdownStyles.menuUnanchored,
          ]}
          accessibilityRole="list"
        >
          {searchable && (
            <View style={dropdownStyles.searchRow}>
              <TextInput
                testID={testID ? `${testID}-search` : undefined}
                value={query}
                onChangeText={setQuery}
                placeholder={searchPlaceholder}
                placeholderTextColor={colors.text.tertiary}
                accessibilityLabel={searchPlaceholder}
                autoCorrect={false}
                autoCapitalize="none"
                style={dropdownStyles.searchInput}
              />
            </View>
          )}
          {searchable && visibleOptions.length === 0 && (
            <Text style={dropdownStyles.emptyText}>{emptySearchLabel}</Text>
          )}
          <ScrollView
            style={[dropdownStyles.menuScroll, { maxHeight: placement.maxHeight }]}
            nestedScrollEnabled
            bounces={false}
            keyboardShouldPersistTaps="handled"
          >
            {visibleOptions.map((option, index) => {
              const active = isSelected(value, option.value);
              const highlighted = index === highlightedIndex;
              return (
                <Pressable
                  key={option.value}
                  onPress={() => handleSelect(option.value)}
                  style={({ pressed }) => [
                    dropdownStyles.option,
                    active && dropdownStyles.optionActive,
                    highlighted && dropdownStyles.optionHighlighted,
                    pressed && dropdownStyles.optionPressed,
                  ]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  testID={testID ? `${testID}-option-${option.value}` : undefined}
                >
                  {option.icon && <View style={dropdownStyles.optionIcon}>{option.icon}</View>}
                  {option.color && (
                    <View style={[dropdownStyles.colorDot, { backgroundColor: option.color }]} />
                  )}
                  <Text
                    style={[dropdownStyles.optionText, active && dropdownStyles.optionTextActive]}
                    numberOfLines={1}
                  >
                    {option.label}
                  </Text>
                  {active && (
                    <View style={dropdownStyles.checkContainer}>
                      <Check size={16} color={colors.primary[500]} />
                    </View>
                  )}
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
}

export function Select(props: SelectProps) {
  if (props.variant === 'chips') {
    const { variant: _, ...rest } = props;
    return <ChipsVariant {...rest} />;
  }

  const { variant: _, ...rest } = props;
  return <DropdownVariant {...rest} />;
}

const chipStyles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  affordance: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 32,
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        userSelect: 'none',
      } as any,
    }),
  },
  affordanceLeft: {
    left: 0,
    alignItems: 'flex-start',
  },
  affordanceRight: {
    right: 0,
    alignItems: 'flex-end',
  },
  scrollContent: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xxs,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transitionProperty: 'background-color, border-color',
        transitionDuration: `${tokens.duration.fast}ms`,
        userSelect: 'none',
      } as any,
    }),
  },
  chipActive: {
    backgroundColor: colors.primary.muted,
    borderColor: colors.primary[500],
  },
  chipInactive: {
    backgroundColor: colors.background.secondary,
    borderColor: colors.border.muted,
  },
  chipPressed: {
    backgroundColor: colors.interactive.hover,
  },
  chipIcon: {
    marginRight: tokens.spacing.xs,
  },
  chipText: {
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.medium,
  },
  chipTextActive: {
    color: colors.primary[500],
  },
  chipTextInactive: {
    color: colors.text.muted,
  },
});

const dropdownStyles = StyleSheet.create({
  wrapper: {
    position: 'relative',
    zIndex: tokens.zIndex.dropdown,
    // Stretch to fill the cross-axis of the parent flex container. RN-Web
    // auto-stretches block elements; native RN doesn't, which made the
    // dropdown trigger shrink to intrinsic content width on iOS (e.g.
    // AssigneePicker rendering as just the first letter + chevron).
    // alignSelf:stretch alone wasn't enough on iOS when the parent VStack
    // sat inside an HStack with flex:1 — width:'100%' forces the trigger
    // to claim the full parent width regardless of parent alignment.
    alignSelf: 'stretch',
    width: '100%',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.background.input,
    borderWidth: 1,
    borderColor: colors.border.input,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.md,
    height: 44,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transitionProperty: 'border-color, box-shadow',
        transitionDuration: `${tokens.duration.fast}ms`,
        userSelect: 'none',
        outlineWidth: 0,
      } as any,
    }),
  },
  triggerOpen: {
    borderColor: colors.border.focus,
  },
  triggerPressed: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
  triggerText: {
    flex: 1,
    fontSize: tokens.fontSize.base,
    color: colors.text.primary,
    marginRight: tokens.spacing.sm,
  },
  triggerPlaceholder: {
    color: colors.text.disabled,
  },
  chevronOpen: {
    transform: [{ rotate: '180deg' }],
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  menu: {
    backgroundColor: colors.background.elevated,
    borderWidth: 1,
    borderColor: colors.border.muted,
    borderRadius: tokens.radius.md,
    overflow: 'hidden',
    ...tokens.shadow?.md,
  },
  menuFloating: {
    position: 'absolute',
  },
  menuUnanchored: {
    alignSelf: 'center',
    marginTop: tokens.spacing.xl,
    width: '90%',
  },
  searchRow: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border.muted,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
  },
  searchInput: {
    color: colors.text.primary,
    fontSize: 14,
    paddingVertical: tokens.spacing.xs,
    ...Platform.select({ web: { outlineStyle: 'none' as never }, default: {} }),
  },
  emptyText: {
    color: colors.text.tertiary,
    fontSize: 13,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
  },
  menuScroll: {
    maxHeight: MENU_MAX_HEIGHT,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transitionProperty: 'background-color',
        transitionDuration: `${tokens.duration.fast}ms`,
      } as any,
    }),
  },
  optionActive: {
    backgroundColor: colors.primary.muted,
  },
  optionHighlighted: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
  optionPressed: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
  optionIcon: {
    marginRight: tokens.spacing.sm,
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radius.full,
    marginRight: tokens.spacing.sm,
  },
  optionText: {
    flex: 1,
    fontSize: tokens.fontSize.base,
    color: colors.text.primary,
  },
  optionTextActive: {
    color: colors.primary[500],
    fontWeight: tokens.fontWeight.medium,
  },
  checkContainer: {
    marginLeft: tokens.spacing.sm,
  },
});
